"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Select,
  Flex,
  Text,
  ActionIcon,
  Tooltip,
  type ComboboxItem,
  type ComboboxLikeRenderOptionInput,
  Box as MantineBox,
  Text as MantineText,
} from "@mantine/core";
import { IconEye, IconEyeOff, IconCheck } from "@tabler/icons-react";
import sortBy from "lodash/sortBy";
import styles from "./styles.module.scss";

type Option = {
  id: string | number;
  value?: string | number;
  label?: string;
  group?: string | null;
  sortOrder?: number;
};

interface Props {
  label: string;
  options?: Option[];
  filterType: string;
  onSelect: (filterType: string, newSelection: Filter[]) => void;
  activeFilters: Filter[];
  groupSortOrder?: string[];
  allowExclude?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  icon?: React.ReactNode;
  onInfoClick?: () => void;
}

const GroupedSelect: React.FC<Props> = ({
  label,
  options,
  filterType,
  onSelect,
  activeFilters,
  allowExclude = true,
  groupSortOrder,
  disabled = false,
  disabledTooltip,
  icon,
  onInfoClick,
}) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const idToName = useMemo(() => {
    const map: Record<string, string> = {};
    options?.forEach((opt) => {
      const selectValue = opt.value !== undefined ? opt.value : opt.id;
      if (selectValue == undefined || selectValue === null || !opt.label) {
        return;
      }
      map[String(selectValue)] = opt.label;
    });
    return map;
  }, [options]);

  const toggleExclude = useCallback(
    (val: string) => {
      setExcluded((prev) => {
        const next = new Set(prev);
        if (next.has(val)) next.delete(val);
        else next.add(val);
        return next;
      });

      // Update filters after state change if there's a selected value
      if (selected) {
        setTimeout(() => {
          const result = [
            {
              id: isNaN(Number(selected)) ? parseInt(selected, 10) || 0 : Number(selected),
              value: isNaN(Number(selected)) ? selected : Number(selected),
              label: idToName[selected] || "",
              state: excluded.has(val) ? ("include" as const) : ("exclude" as const), // Toggle the state
            },
          ];
          onSelect(filterType, result);
        }, 0);
      }
    },
    [selected, idToName, filterType, onSelect, excluded]
  );

  const formattedOptions = useMemo(() => {
    if (!options?.length) return [];

    const map: Record<string, ComboboxItem[]> = {};
    options.forEach(({ id, label, group, value }) => {
      const selectValue = value !== undefined ? value : id;
      const entry: ComboboxItem = {
        label: excluded.has(String(selectValue)) ? `${label}\u200B` : label || String(selectValue),
        value: String(selectValue),
      };
      const key = group ?? "__ungrouped";
      map[key] = map[key] || [];
      map[key].push(entry);
    });

    const ungrouped = sortBy(map["__ungrouped"] || [], (item) => item.label.toLowerCase());

    const grouped = Object.keys(map)
      .filter((g) => g !== "__ungrouped")
      .map((g) => ({
        group: g,
        items: sortBy(map[g], (item) => item.label.toLowerCase()),
      }));

    let sorted = [...ungrouped, ...grouped];

    if (groupSortOrder && groupSortOrder.length) {
      sorted = sortBy(sorted, (item) => {
        if ("group" in item) return groupSortOrder.indexOf(item.group);
        return Infinity;
      });
    }

    return sorted;
  }, [options, excluded, groupSortOrder]);

  const renderOption = (item: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
    const { option } = item;
    const isSelected = selected === option.value;
    const isExcluded = excluded.has(option.value);

    const onClickHandler = (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleExclude(option.value);
    };

    return (
      <Flex justify="space-between" align="center" w="100%" pr={0} className={styles.optionRow}>
        <MantineBox className={styles.optionContent}>
          {isSelected && <IconCheck size={16} className={styles.checkIcon} />}
          <Text size="xs" ml="24px">
            {option.label.replace("\u200B", "").trim()}
          </Text>
        </MantineBox>

        {allowExclude && isSelected && (
          <Tooltip
            label={isExcluded ? "EXCLUDED" : "INCLUDED"}
            openDelay={500}
            withArrow
            zIndex={1100}
          >
            <ActionIcon
              onClick={onClickHandler}
              variant="transparent"
              data-excluded={isExcluded}
              aria-hidden={true}
            >
              {isExcluded ? (
                <IconEyeOff size={16} color="black" />
              ) : (
                <IconEye size={16} color="black" />
              )}
            </ActionIcon>
          </Tooltip>
        )}
      </Flex>
    );
  };

  useEffect(() => {
    const initial = activeFilters[0]?.value?.toString() ?? null;
    setSelected(initial);

    const newExcluded = new Set(
      activeFilters
        .filter((filter) => filter.state === "exclude")
        .map((f) => f?.value?.toString())
        .filter(Boolean)
    );
    setExcluded(newExcluded);
  }, [activeFilters]);

  const handleChange = (val: string | null) => {
    setSelected(val);

    const result = val
      ? [
          {
            id: isNaN(Number(val)) ? parseInt(val, 10) || 0 : Number(val),
            value: isNaN(Number(val)) ? val : Number(val), // Convert to number if it's numeric
            label: idToName[val] || "",
            state: excluded.has(val) ? ("exclude" as const) : ("include" as const),
          },
        ]
      : [];

    onSelect(filterType, result);
  };

  const selectComponent = (
    <Select
      label={
        icon ? (
          <MantineBox className={styles.labelContainer}>
            <MantineText span inherit>
              {label}
            </MantineText>
            <ActionIcon
              type="button"
              onClick={onInfoClick}
              className={styles.infoButton}
              aria-label={`Open information for ${label}`}
              variant="transparent"
              size="sm"
            >
              {icon}
            </ActionIcon>
          </MantineBox>
        ) : (
          label
        )
      }
      placeholder={`Search ${label.toLowerCase()}...`}
      value={selected}
      onChange={handleChange}
      data={formattedOptions}
      renderOption={renderOption}
      size="sm"
      disabled={disabled}
      maxDropdownHeight={250}
      chevronColor="black"
      comboboxProps={{
        position: "bottom",
        middlewares: { flip: true, shift: false },
      }}
    />
  );

  return (
    <>
      {disabled && disabledTooltip ? (
        <Tooltip label={disabledTooltip} openDelay={500} withArrow>
          {selectComponent}
        </Tooltip>
      ) : (
        selectComponent
      )}
    </>
  );
};

export default GroupedSelect;
